import { FC, useLayoutEffect } from 'react';
import { useTypedDispatch, useTypedSelector } from '@/store/hooks';
import { Accordion, Badge, Flex, SimpleGrid, Text } from '@mantine/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AccordionOptionCard } from '@/components/ui/AccordionOptionCard';
import { UUID } from '@/types/types';
import { setLoading } from '@/store/slices/appSlice';
import { creatorActions } from '@/store/slices/creatorSlice';
import { getFileSrc } from '@/utils/utilities';
import { useStartCollectionMutation } from '@/hooks/useStartCollectionMutation';
import { useContent } from '@/providers/ContentProvider';
import ProjectApi from '@/api/ProjectApi';
import PresetApi from '@/api/PresetApi';
import { IconPlus } from '@tabler/icons-react';

const baseSlug = 'presets.';

export const Presets: FC = () => {
	const dispatch = useTypedDispatch();

	const { getContent } = useContent();

	const { selectedProductModuleCollection, selectedPreset, options, project } =
		useTypedSelector((app) => app.creator);

	const startCollectionMutation = useStartCollectionMutation();
	const clearProjectMutation = useMutation({
		mutationFn: ProjectApi.clear,
		onSuccess: () => {
			dispatch(
				creatorActions.updateProject({ price: 0, creatorProductModules: [] })
			);
		},
	});

	const presetsQuery = useQuery({
		queryKey: [
			PresetApi.queryKey,
			{
				sortBy: {
					key: 'listOrder',
					order: 'ASC',
				},
			},
			{
				'filter.productModuleCollection.id':
					selectedProductModuleCollection?.id,
			},
		],
		queryFn: PresetApi.getAll,
		enabled: !!selectedProductModuleCollection,
	});

	const projectFromPresetMutation = useMutation({
		mutationFn: ProjectApi.createFromPreset,
		onSuccess: (data) => {
			dispatch(setLoading(false));
			dispatch(creatorActions.createFromPreset(data));
		},
	});

	const handleSelectPreset = (presetId: UUID | null) => () => {
		if (presetId) {
			dispatch(setLoading(true));
			projectFromPresetMutation.mutate({
				presetId,
				projectId: project.id,
			});
		} else {
			dispatch(
				creatorActions.updateCreator({
					selectedPreset: null,
				})
			);
			if (selectedProductModuleCollection) {
				clearProjectMutation.mutate(project.id, {
					onSuccess: () => {
						startCollectionMutation.mutate(selectedProductModuleCollection.id);
					},
				});
			}
		}
	};

	useLayoutEffect(() => {
		if (!options.lockedPresetId || !project.id) return;

		handleSelectPreset(options.lockedPresetId)();
	}, [options.lockedPresetId, project.id]);

	if (options.isPresetMode) return null;

	return options.lockedPresetId ? null : (
		<Accordion.Item value="presets">
			<Accordion.Control disabled={!selectedProductModuleCollection}>
				<Flex justify="space-between">
					Zestawy
					{selectedProductModuleCollection && (
						<Badge>
							<Text size="sm">
								{selectedPreset
									? selectedPreset.name
									: getContent(`${baseSlug}ownProjectButtonText`)}
							</Text>
						</Badge>
					)}
				</Flex>
			</Accordion.Control>
			<Accordion.Panel>
				<SimpleGrid py="md" cols={3} spacing="xs">
					<AccordionOptionCard
						isSelected={!selectedPreset}
						name="Własny projekt"
						Icon={IconPlus}
						onSelect={handleSelectPreset(null)}
						description=""
					/>
					{presetsQuery.data?.map((preset) => {
						const isSelected = selectedPreset?.id === preset.id;

						return (
							<AccordionOptionCard
								key={preset.id}
								id={preset.id}
								isSelected={isSelected}
								name={preset.name}
								description=""
								imagePath={getFileSrc(preset?.image?.path)}
								onSelect={handleSelectPreset(preset.id)}
							/>
						);
					})}
				</SimpleGrid>
			</Accordion.Panel>
		</Accordion.Item>
	);
};
